// Four bots, one per role, so the engine can be played thousands of times
// without four phones. Each one answers the same question a person answers:
// "given what this role can legally do right now, and what the town is short
// of, what do I spend this worker on?"
//
// They are NOT meant to play perfectly. They are meant to play PLAUSIBLY, so
// that a number that is broken shows up as a bot that always presses the same
// button, or a loss the bots can never dig out of. A dial that only looks
// balanced because the bot is too dim to exploit it is not balanced; that is
// what the `greedy` personality is for - it always takes the best immediate
// payoff, which is how a real table finds a degenerate line.

import { checkAction } from '@/engine/roleActions';
import { civFor, actAction, doneAction } from '@/engine/game';
import {
  cityOf, workable, annexable, BUILDING_ORDER, hexWithRoom, canBuildType, foodCap, goodsCap,
  count as cityCount,
} from '@/engine/city';
import {
  hallOf, councilOf, waitingOf, offerOf, seatUsed, COUNCIL_SEATS, buysOf, cardPrice, HALL_CARDS, COUNSELORS,
} from '@/engine/hall';
import {
  loreOf, deducedAspects, PRINCIPLES,
} from '@/engine/lore';
import {
  MACHINES, machineForTier, onBench, inputsOf, canFeed, lawsLeft, firingsOf, natureFromKey, applyLaw,
} from '@/engine/machines';
import { machineStands } from '@/engine/city';
import { housingOf } from '@/engine/effects';
import { techReachable, TECHS } from '@/engine/cards';
import { rivalsNear } from '@/engine/rivals';
import {
  techPrice, makeable, cardEffect, cardGives, cardDraws, bonusesFor,
} from '@/engine/roleActions';
import {
  villagesOf, canFoundAt, unexploredDirections, benchPrinciples, canRaiseCity,
} from '@/engine/roleActions';
import { siteCentre } from '@/engine/map';
import { goalOf } from '@/engine/cards';
import { VILLAGE } from '@/engine/constants';
import { workAt } from '@/engine/terrain';
import {
  unitsOf, raidersOf, kindOf, stepsFrom, foesNear, tradePreview, raiderTarget, raiderStep,
} from '@/engine/units';

const ok = (state, role, id, params) => checkAction(state, role, id, params).ok;
// Which technology a building waits on, for a demand for the building.
const BUILDINGS_NEED = { mill: 'wheel', warehouse: 'pottery', kiln: 'kilns', still: 'distilling', crucible: 'assaying' };

/** What the Stewards can do today toward the people's demand, if anything. */
const demandTrade = (state, civ) => {
  const d = civ.demand;
  if (!d || civ.workers < 2) return null;
  if (d.kind === 'building' && ok(state, 'trade', 'build', { type: d.target })) return actAction('trade', 'build', { type: d.target });
  if (d.kind === 'homes' && ok(state, 'trade', 'build', { type: 'house' })) return actAction('trade', 'build', { type: 'house' });
  if (d.kind === 'warband' && ok(state, 'trade', 'train', { kind: 'warband' })) return actAction('trade', 'train', { kind: 'warband' });
  if (d.kind === 'village' && !unitsOf(civ).some((u) => u.kind === 'pioneer') && ok(state, 'trade', 'train', { kind: 'pioneer' })) return actAction('trade', 'train', { kind: 'pioneer' });
  return null;
};

/** What the town is most short of right now, as weights on each resource. */
export const needs = (civ, meta) => {
  const eat = Math.ceil(civ.population / 2);
  return {
    // Food is only interesting when the larder will not cover the night.
    food: civ.food < eat + 2 ? 6 : (civ.food < eat + 5 ? 2 : 0.3),
    goods: civ.goods < 3 ? 3 : 1,
    coin: civ.coin < 3 ? 2.5 : 1,
    knowledge: 1.5,
    // Happiness above the unhappy line is nearly worthless: it caps at 10
    // and the only thing it buys is that everybody turns out. The bots used
    // to chase it and pin it at max for whole games.
    // ...but it FADES now, and families only arrive at 8: a point between
    // 7 and 8 is a person, so it is worth something up to there.
    happiness: civ.happiness <= 4 ? 8 : (civ.happiness <= 6 ? 3 : (civ.happiness < 8 ? 1.6 : 0.15)),
    // A PERSON IS A WORKER EVERY ROUND FOR THE REST OF THE GAME. It is the
    // only compounding thing in the game and the first bots priced it at
    // about a coin, so they almost never played Grow - the single card in
    // the starting ten that makes one - and every town stayed at four people
    // all game, which is what made the band unaffordable and the Frontier
    // look broken. Worth roughly what it will still earn.
    population: civ.population < 8 ? Math.max(3, 12 - meta.round * 0.5) : 1,
    threat: civ.threat >= 6 ? 7 : (civ.threat >= 4 ? 2 : 0.5),
    stage: civ.goal ? 5 : 0,
    ...(civ.demand?.kind === 'glad' && (civ.happiness || 0) < civ.demand.mark ? { happiness: 8 } : {}),
    ...(civ.demand?.kind === 'law' ? { law: 6 } : {}),
  };
};

// ------------------------------------------------------------------ Stewards
const tradeMove = (state, want, style, meta) => {
  const civ = civFor(state, 'trade');
  const city = cityOf(civ);
  const hands = civ.workers;
  const eat = Math.ceil(civ.population / 2);

  // A VILLAGE IS THE BEST THING THE TOWN CAN BUY. It sends up what the
  // ground makes every night, for ever, with nobody tending it - so a
  // pioneer pays for itself in a handful of rounds and then keeps paying.
  // The first bot priced it like any other errand and never planted one,
  // which left the whole expansion half of the game untested.
  const mine0 = unitsOf(civ);
  // But nothing out there is worth planting while raiders are walking at
  // the city and nobody can stop them. A warband first, when the map says
  // so; the first bot trained three pioneers a game and a fifth of a
  // warband, and lost a quarter of its games to a trickle of wolves.
  const warbands0 = mine0.filter((u) => u.kind === 'warband').length;
  // "Danger" is raiders actually walking, or no warband at all - a camp
  // merely sitting on the shore is not, or no pioneer would ever be trained
  // again, since a camp is on the map for most of most games.
  const danger = raidersOf(civ).length > 0 || warbands0 < 1;
  // One warband per raider abroad, and one more: a raider nobody can reach
  // in time is a village sacked.
  const enough = Math.max(2, raidersOf(civ).length + 1);
  // Not with the larder bare: playtest 10's bot trained a pioneer with food
  // at 1 and a 3-food night coming.
  const fed = civ.food >= eat * 2;
  // SUPPER FIRST: with tonight's food not in the larder, a field before
  // anything else (the first bot trained and built first and starved five
  // times a game).
  if (civ.food < eat + 1 && hands >= 1) {
    const field = workable(civ).find((b) => b.type === 'field');
    if (field) return actAction('trade', 'work', { building: field.id });
    if (ok(state, 'trade', 'buy-grain')) return actAction('trade', 'buy-grain');
  }
  if (hands >= 2 && danger && warbands0 < enough && (fed || raidersOf(civ).length) && ok(state, 'trade', 'train', { kind: 'warband' })) {
    return actAction('trade', 'train', { kind: 'warband' });
  }
  if (fed && hands >= 2 && meta.round >= 2
    && !mine0.some((u) => u.kind === 'pioneer')
    && villagesOf(civ).length < 4
    && ok(state, 'trade', 'train', { kind: 'pioneer' })) {
    return actAction('trade', 'train', { kind: 'pioneer' });
  }
  // A town ready to be a city becomes one; a city makes what is short.
  const ready = villagesOf(civ).find((v) => canRaiseCity(civ, v));
  if (ready && hands >= 2 && ok(state, 'trade', 'raise-city', { village: ready.id })) return actAction('trade', 'raise-city', { village: ready.id });
  for (const v of villagesOf(civ).filter((x) => x.city)) {
    const short = ['food', 'goods', 'coin', 'knowledge'].sort((a, b) => (civ[a] || 0) - (civ[b] || 0))[0];
    const focus = civ.food < eat + 2 ? 'food' : short;
    if (v.focus !== focus && ok(state, 'trade', 'city-focus', { village: v.id, kind: focus })) return actAction('trade', 'city-focus', { village: v.id, kind: focus });
    if (!v.district && hands >= 2 && ok(state, 'trade', 'city-district', { village: v.id, district: 'school' })) return actAction('trade', 'city-district', { village: v.id, district: 'school' });
  }
  // The people's demand, once tonight's supper is safe.
  const promised = demandTrade(state, civ);
  if (promised) return promised;
  // A full town stops growing: a House before anything else that can wait.
  if (hands >= 2 && civ.population >= housingOf(civ) - 1 && ok(state, 'trade', 'build', { type: 'house' })) {
    return actAction('trade', 'build', { type: 'house' });
  }
  // A larder that overflows every night wants a granary, and a town that
  // has never once built one is not playing the Stewards' game.
  if (hands >= 2 && civ.food >= foodCap(civ) - 1 && cityCount(civ, 'granary') < 1
    && ok(state, 'trade', 'build', { type: 'granary' })) {
    return actAction('trade', 'build', { type: 'granary' });
  }
  if (hands >= 2 && civ.goods >= goodsCap(civ) && ok(state, 'trade', 'build', { type: 'warehouse' })) {
    return actAction('trade', 'build', { type: 'warehouse' });
  }
  // The Scholars' machines, as soon as the town knows how: they are the
  // only way up the tree past tier 2.
  if (hands >= 2) {
    const m = MACHINES.find((x) => canBuildType(civ, x.id) && hexWithRoom(city, x.id) && ok(state, 'trade', 'build', { type: x.id }));
    if (m) return actAction('trade', 'build', { type: m.id });
  }
  // One library, once the Scholars have something to climb.
  if (hands >= 2 && meta.round >= 4 && cityCount(civ, 'library') < 1 && civ.goods >= 3
    && ok(state, 'trade', 'build', { type: 'library' })) {
    return actAction('trade', 'build', { type: 'library' });
  }

  // The great work, when the town can spare it and the era is getting on.
  if (want.stage >= 5 && ok(state, 'trade', 'build-monument')) return actAction('trade', 'build-monument');

  // Hungry: buy grain before anything else, then staff a field.
  if (civ.food < eat && ok(state, 'trade', 'buy-grain')) return actAction('trade', 'buy-grain');

  // Staff what pays for what the town lacks.
  const free = workable(civ);
  if (free.length && hands >= 1) {
    const value = (b) => {
      if (b.type === 'field') return civ.food >= foodCap(civ) ? 0.2 : want.food;
      if (b.type === 'workshop') return want.goods;
      // A market shift with nothing on the stall is a wasted half-day.
      if (b.type === 'market') return civ.goods >= 2 ? want.coin : 0;
      return 0.1;
    };
    const best = free.slice().sort((a, b) => value(b) - value(a))[0];
    if (value(best) >= 1 || style === 'greedy') return actAction('trade', 'work', { building: best.id });
  }

  // Build, if there is room and the town is not starving.
  if (hands >= 2) {
    const order = style === 'builder'
      ? ['field', 'workshop', 'market', 'granary', 'warehouse', 'mill']
      : ['field', 'market', 'workshop', 'granary', 'warehouse', 'mill'];
    // Skip a field when the larder is full, but go on down the list rather
    // than giving up on building altogether.
    const wanted = order.filter((t) => !(t === 'field' && want.food <= 2));
    const pickType = wanted.find((t) => BUILDING_ORDER.includes(t)
      && canBuildType(civ, t) && hexWithRoom(city, t)
      && ok(state, 'trade', 'build', { type: t }));
    if (pickType) return actAction('trade', 'build', { type: pickType });
  }

  // The Frontier's army is the Stewards' goods. Keep a warband or two in
  // the field, and a pioneer whenever there is anywhere to put one.
  const mine = unitsOf(civ);
  const warbands = mine.filter((u) => u.kind === 'warband').length;
  if (hands >= 2) {
    if (warbands < (civ.threat >= 3 ? 3 : 2) && ok(state, 'trade', 'train', { kind: 'warband' })) {
      return actAction('trade', 'train', { kind: 'warband' });
    }
  }
  const burnt = city.buildings.find((b) => b.burned);
  if (burnt && ok(state, 'trade', 'repair', { building: burnt.id })) return actAction('trade', 'repair', { building: burnt.id });
  const hurt = mine.find((u) => ok(state, 'trade', 'reinforce', { unit: u.id }));
  if (hurt && hands >= 1) return actAction('trade', 'reinforce', { unit: hurt.id });

  // A caravan goes to a settlement: a full load, and it feeds it.
  const stop = villagesOf(civ).filter((v) => !v.damaged)
    .sort((a, b) => a.size - b.size)[0];
  if (hands >= 2 && stop && ok(state, 'trade', 'caravan', { village: stop.id })) {
    return actAction('trade', 'caravan', { village: stop.id });
  }
  if (ok(state, 'trade', 'buy-grain') && want.food >= 2) return actAction('trade', 'buy-grain');

  // Out of room? Take in land while there is a day left.
  // Out of room? Take in land. This has to come BEFORE haggling, or a city
  // with nowhere left to build spends every spare shift squeezing coin.
  const land = annexable(civ);
  const roomless = !BUILDING_ORDER.some((t) => canBuildType(civ, t) && hexWithRoom(city, t));
  if (land.length && roomless) {
    const hex = { q: land[0][0], r: land[0][1] };
    if (ok(state, 'trade', 'annex', { hex })) return actAction('trade', 'annex', { hex });
  }

  const burned = city.buildings.find((b) => b.burned);
  if (burned && ok(state, 'trade', 'repair', { building: burned.id })) return actAction('trade', 'repair', { building: burned.id });

  if (hands >= 1 && ok(state, 'trade', 'haggle')) return actAction('trade', 'haggle');
  return null;
};

// ---------------------------------------------------------------- Scholars
const scienceMove = (state, want, style) => {
  const civ = civFor(state, 'science');
  const lore = loreOf(civ);
  if (!lore) return null;

  if (want.stage >= 5 && ok(state, 'science', 'build-monument')) return actAction('science', 'build-monument');

  // Aim at a technology whenever the notebook is sure enough of a pair.
  const reachable = Object.keys(TECHS).filter((t) => techReachable(civ, t) && !(civ.techs || []).includes(t));
  let benchTechs = reachable.filter((t) => onBench(TECHS[t].tier));
  // The people asked for a technology: it, or the prerequisite it waits on,
  // comes first (engine/demands.js).
  const asked = civ.demand?.kind === 'tech' ? civ.demand.target : (civ.demand?.kind === 'building' ? BUILDINGS_NEED[civ.demand.target] : null);
  if (asked) {
    const step = benchTechs.includes(asked) ? [asked] : benchTechs.filter((t) => (TECHS[asked]?.needs || []).includes(t));
    const sure = step.length ? bestPair(civ, lore, step, style) : null;
    if (sure) benchTechs = step;
  }
  const pair = bestPair(civ, lore, benchTechs, style);
  if (pair && ok(state, 'science', 'mix', pair)) return actAction('science', 'mix', pair);
  // A pair is known but the purse cannot pay for the writing-down: read.
  if (pair && civ.knowledge < techPrice(civ, pair.target) && ok(state, 'science', 'study')) return actAction('science', 'study');

  // The machines: make what the shelf already holds, else fire toward it.
  const climb = machineMove(state, civ, lore, reachable);
  if (climb) return climb;

  // Otherwise learn: mix the two the notebook is least sure of.
  if (benchTechs.length || reachable.some((t) => !onBench(TECHS[t].tier))) {
    const know = deducedAspects(lore);
    const blind = PRINCIPLES
      .map((p) => [p, ASPECT_KEYS.filter((k) => know[p]?.[k] === undefined).length])
      .sort((a, b) => b[1] - a[1]);
    if (blind[0] && blind[0][1] > 0) {
      const params = { a: blind[0][0], b: blind[1][0], target: '' };
      if (ok(state, 'science', 'mix', params)) return actAction('science', 'mix', params);
    }
  }

  // Publishing is free knowledge once the notebook is certain: a theory it
  // can prove pays 2 back and teaches somebody a worker.
  const sure = deducedAspects(lore);
  const provable = benchPrinciples(civ).find((p) => ASPECT_KEYS.every((k) => sure[p]?.[k] !== undefined)
    && ok(state, 'science', 'publish', {
      principle: p,
      nature: { heat: sure[p].heat, moisture: sure[p].moisture, weight: sure[p].weight },
      teach: 'military',
    }));
  if (provable) {
    return actAction('science', 'publish', {
      principle: provable,
      nature: {
        heat: sure[provable].heat, moisture: sure[provable].moisture, weight: sure[provable].weight,
      },
      teach: civ.threat >= 4 ? 'military' : 'trade',
    });
  }

  if (ok(state, 'science', 'study')) return actAction('science', 'study');
  // The stones only with knowledge to spare: the first bot asked them
  // whenever it had 2, and so never saved the 5 a technology costs - it
  // learned nothing in its first year (the growth curve, 2026-10-16).
  const cheapest = Math.min(99, ...benchTechs.map((t) => techPrice(civ, t)));
  if (civ.knowledge >= cheapest + 2) {
    const p = benchPrinciples(civ).find((x) => ok(state, 'science', 'hint', { principle: x }));
    if (p) return actAction('science', 'hint', { principle: p });
  }
  if (ok(state, 'science', 'ponder')) return actAction('science', 'ponder');
  return null;
};

// The engine's aspect keys, not the words a person reads ('hot' is the
// WORD for heat: true).
const ASPECT_KEYS = ['heat', 'moisture', 'weight'];

// What the NOTEBOOK says a principle could still be - the pencil marks, not
// just the ink. The first bot reasoned only from what had been told to it
// outright, which is far dumber than the board a player is looking at: the
// phone runs this same solver and fills in everything every arrangement
// agrees on. Tuning the game against a bot that ignores its own notebook
// would have been tuning it against a strawman.


/** The probability a pair makes `target`, from what the notebook knows. */
const pairChance = (lore, know, a, b, need) => {
  const keys = Object.keys(need);
  let p = 1;
  keys.forEach((k) => {
    const ka = know[a]?.[k];
    const kb = know[b]?.[k];
    // A settled aspect is certain; an open one is a coin, and BOTH have to
    // land on the value the reaction wants.
    p *= (ka === undefined ? 0.5 : (ka === need[k] ? 1 : 0));
    p *= (kb === undefined ? 0.5 : (kb === need[k] ? 1 : 0));
  });
  return p;
};

/** A pair the notebook is confident enough about, or null. */
const bestPair = (civ, lore, reachable, style) => {
  const know = deducedAspects(lore);
  const P = PRINCIPLES;
  const book = lore;
  // A miss costs the worker and burns the pair, so a careful table waits for
  // certainty and a greedy one takes anything better than a coin flip.
  const floor = style === 'greedy' ? 0.5 : 0.99;
  let best = null;
  reachable.forEach((target) => {
    const need = lore.reactions?.[target];
    if (!need || !Object.keys(need).length) return;
    for (let i = 0; i < P.length; i += 1) {
      for (let j = i + 1; j < P.length; j += 1) {
        const chance = pairChance({ ...lore, ...book, reactions: lore.reactions }, know, P[i], P[j], need);
        if (chance >= floor && (!best || chance > best.chance)) {
          best = { chance, a: P[i], b: P[j], target };
        }
      }
    }
  });
  return best ? { a: best.a, b: best.b, target: best.target } : null;
};

// The ladder. The bot reasons the way the sheet lets a player: the laws a
// machine could still have, given its firings and what the inputs are known
// to be. It makes a technology the moment the shelf holds the makings, and
// otherwise fires the pair most likely to give what the lowest deep
// technology within reach needs - stocking the machine below first when the
// shelf is too bare to feed this one.
const machineMove = (state, civ, lore, reachable) => {
  const deep = reachable.filter((t) => !onBench(TECHS[t].tier) && machineForTier(TECHS[t].tier)
    && machineStands(civ, machineForTier(TECHS[t].tier).id))
    .sort((a, b) => TECHS[a].tier - TECHS[b].tier);
  if (!deep.length) return null;
  const made = deep.find((t) => makeable(civ, t));
  if (made) {
    if (ok(state, 'science', 'make', { target: made })) return actAction('science', 'make', { target: made });
    if (civ.knowledge < techPrice(civ, made) && ok(state, 'science', 'study')) return actAction('science', 'study');
  }
  const know = deducedAspects(lore);
  const natureOf = (machineId) => (x) => {
    const m = MACHINES.find((y) => y.id === machineId);
    if (m.from) return natureFromKey(x);
    const k = know[x] || {};
    return ASPECT_KEYS.every((a) => k[a] !== undefined) ? k : null;
  };
  const fireToward = (machineId, need) => {
    const read = natureOf(machineId);
    const left = lawsLeft(machineId, firingsOf(civ, machineId), read);
    const ins = inputsOf(civ, machineId);
    let best = null;
    ins.forEach((a) => ins.forEach((b) => {
      if (!canFeed(civ, machineId, a, b)) return;
      const na = read(a);
      const nb = read(b);
      let chance = 1;
      Object.entries(need || {}).forEach(([k, v]) => {
        if (!na || !nb) { chance *= 0.5; return; }
        const laws = left[k];
        chance *= laws.filter((law) => applyLaw(law, na, nb) === v).length / Math.max(1, laws.length);
      });
      // Among equals, a pair not fired before teaches more.
      const tried = firingsOf(civ, machineId).some((f) => f.a === a && f.b === b);
      const score = chance - (tried ? 0.01 : 0);
      if (!best || score > best.score) best = { a, b, chance, score };
    }));
    // Nothing on hand can give it: say so, and let the caller stock up.
    if (best && best.chance > 0 && ok(state, 'science', 'fire', { machine: machineId, a: best.a, b: best.b })) {
      return actAction('science', 'fire', { machine: machineId, a: best.a, b: best.b });
    }
    return null;
  };
  for (const t of deep) {
    const m = machineForTier(TECHS[t].tier);
    const need = lore.reactions?.[t];
    const go = fireToward(m.id, need);
    if (go) return go;
    // Too little on the shelf to feed it: stock up from the machine below.
    if (m.from && machineStands(civ, m.from)) {
      const below = fireToward(m.from, {});
      if (below) return below;
    }
  }
  return null;
};

// ---------------------------------------------------------------- Frontier
// A WORKER IS ONE UNIT'S TURN. The bot spends its orders on the units that
// most need to do something: intercept a raider walking at your stuff,
// clear a camp, plant a village, work the ground.
const militaryMove = (state, want, style) => {
  const civ = civFor(state, 'military');
  // A worker is one unit's turn, and the list is walked in order - so the
  // warband, always first and always with somewhere to go, took every order
  // and a pioneer stood at the muster point all game. Pioneers go first
  // unless raiders are actually abroad.
  const units = unitsOf(civ).slice().sort((a, b) => {
    const rank = (u) => (kindOf(u).canFound && !raidersOf(civ).length ? 0 : 1);
    return rank(a) - rank(b);
  });
  if (!units.length) return null;

  if (want.stage >= 5 && ok(state, 'military', 'build-monument')) return actAction('military', 'build-monument');

  // 1. Anything standing next to a raider kills it: a raider that gets
  //    through wrecks a village or fires a building.
  for (const u of units) {
    if (!kindOf(u).canAttack) continue;
    const raider = foesNear(civ, u).find((f) => f.foe === 'raider');
    if (raider && ok(state, 'military', 'fight', { unit: u.id, foe: raider.id })) {
      return actAction('military', 'fight', { unit: u.id, foe: raider.id });
    }
  }

  // 2. Repair what was wrecked, and plant what can be planted.
  for (const u of units) {
    if (ok(state, 'military', 'repair-village', { unit: u.id })) {
      return actAction('military', 'repair-village', { unit: u.id });
    }
    if (ok(state, 'military', 'found', { unit: u.id })) {
      return actAction('military', 'found', { unit: u.id });
    }
  }

  // 2b. The people want a camp gone: the nearest one, even at a price.
  if (civ.demand?.kind === 'camp') {
    for (const u of units) {
      if (!kindOf(u).canAttack) continue;
      const camp = foesNear(civ, u).find((f) => f.foe === 'camp');
      if (camp && ok(state, 'military', 'fight', { unit: u.id, foe: camp.id })) return actAction('military', 'fight', { unit: u.id, foe: camp.id });
    }
  }

  // 3. Clear a camp when the trade is worth taking. Attrition means this is
  //    never hopeless, only expensive - so the question is whether we come
  //    out of the exchange still standing.
  for (const u of units) {
    if (!kindOf(u).canAttack) continue;
    const camp = foesNear(civ, u).find((f) => f.foe === 'camp');
    if (!camp) continue;
    const t = tradePreview(civ, u, camp);
    const worth = style === 'greedy' ? u.strength > t.take : u.strength - t.take >= 1;
    if (worth && ok(state, 'military', 'fight', { unit: u.id, foe: camp.id })) {
      return actAction('military', 'fight', { unit: u.id, foe: camp.id });
    }
  }

  // 3b. A camp too big to fight, with coin to spare: pay it to go.
  for (const u of units) {
    const camp = foesNear(civ, u).find((f) => f.foe === 'camp');
    if (camp && civ.coin >= camp.strength * 2 + 4 && ok(state, 'military', 'parley', { unit: u.id, foe: camp.id })) {
      return actAction('military', 'parley', { unit: u.id, foe: camp.id });
    }
    // A hostile people beside you: coin makes them wary, or a warband makes them small.
    const rival = rivalsNear(civ, u).find((r) => r.stance === 'hostile');
    if (rival) {
      if (civ.coin >= rival.power * 2 + 4 && ok(state, 'military', 'parley', { unit: u.id, foe: rival.id })) {
        return actAction('military', 'parley', { unit: u.id, foe: rival.id });
      }
      if (kindOf(u).canAttack && u.strength >= rival.power && ok(state, 'military', 'raid', { unit: u.id, foe: rival.id })) {
        return actAction('military', 'raid', { unit: u.id, foe: rival.id });
      }
    }
  }

  // 3c. Two small warbands beside each other, with a camp out there too big
  //     for either: fold them together (merge, after playtest 12).
  for (const u of units) {
    if (u.kind !== 'warband') continue;
    const camp = (civ.menaces || []).slice().sort((a, b) => dist(u, a) - dist(u, b))[0];
    if (!camp || camp.strength < u.strength) continue;
    const mate = units.find((o) => o.id !== u.id && o.kind === 'warband' && dist(u, o) <= 1 && ok(state, 'military', 'merge', { unit: u.id, into: o.id }));
    if (mate) return actAction('military', 'merge', { unit: u.id, into: mate.id });
  }

  // 4. Walk. Pioneers go looking for somewhere to plant; warbands head for
  //    the nearest raider, then the nearest camp; scouts go into the fog.
  //    One warband stays HOME while raiders are abroad: a unit on the city
  //    hex is what stops them at the gate.
  const guard = raidersOf(civ).length ? units.find((u) => u.kind === 'warband' && u.q === 0 && u.r === 0) : null;
  for (const u of units) {
    if (guard && u.id === guard.id) continue;
    const step = walkFor(state, civ, u, want);
    if (step) return step;
  }

  // 5. Nothing better: work the ground.
  for (const u of units) {
    if (ok(state, 'military', 'forage', { unit: u.id })) {
      return actAction('military', 'forage', { unit: u.id });
    }
  }
  return null;
};

const dist = (a, b) => Math.max(
  Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs((a.q + a.r) - (b.q + b.r)),
);

/**
 * Where this warband should stand to stop a raider: walk each raider's path
 * toward its target (the engine's own raiderStep), and take the first hex on
 * it this unit reaches no later than the raider does. The raider nearest its
 * target goes first. Null if none can be caught.
 */
const interceptFor = (civ, u) => {
  const raiders = raidersOf(civ).map((x) => ({ x, t: raiderTarget(civ, x) })).filter((p) => p.t)
    .sort((a, b) => a.t.d - b.t.d);
  // Two actions a worker, so a unit covers about two hexes a day.
  const pace = 2;
  for (const { x } of raiders) {
    let at = x;
    for (let n = 1; n <= 8; n += 1) {
      const next = raiderStep({ ...civ, raiders: raidersOf(civ).map((y) => (y.id === x.id ? { ...y, q: at.q, r: at.r } : y)) }, { ...x, q: at.q, r: at.r });
      if (!next) break;
      at = { q: next.q, r: next.r };
      if (Math.ceil(dist(u, at) / pace) <= n) return at;
    }
  }
  return null;
};

const walkFor = (state, civ, u, want) => {
  const steps = stepsFrom(civ, u).filter(([q, r]) => ok(state, 'military', 'move', { unit: u.id, to: { q, r } }));
  if (!steps.length) return null;
  const go = (pick) => actAction('military', 'move', { unit: u.id, to: { q: pick[0], r: pick[1] } });

  // A pioneer's whole job is to find ground worth planting on. Standing at
  // the city, nothing beside it is ever foundable - a village may not be
  // planted inside ring 3 - so it has to walk OUTWARD until it is. The
  // first bot had no such rule and its pioneers stood in the square all
  // game, which is why no village was ever planted in a thousand games.
  if (kindOf(u).canFound) {
    const spot = steps.find(([q, r]) => canFoundAt(civ, q, r));
    if (spot) return go(spot);
    const out = steps
      .map(([q, r]) => ({ q, r, d: Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) }))
      .sort((a, b) => b.d - a.d)[0];
    const here = Math.max(Math.abs(u.q), Math.abs(u.r), Math.abs(u.q + u.r));
    if (out && out.d > here) return go([out.q, out.r]);
    if (out) return go([out.q, out.r]);
  }
  // Raiders first: they are walking at something of yours right now. Get IN
  // FRONT of one, not behind it: a unit standing on a raider's next hex stops
  // it there (blockedBy), and then it can be fought. The first bot chased
  // the nearest raider from behind and never caught one before it arrived,
  // which is most of why the bots were sacked three games in four (after
  // playtest 12, Matt: "is there any way we can make the bots smarter?").
  if (kindOf(u).canAttack) {
    const block = interceptFor(civ, u);
    if (block) {
      const closer = steps.slice().sort((a, b) => dist({ q: a[0], r: a[1] }, block) - dist({ q: b[0], r: b[1] }, block))[0];
      if (closer && dist({ q: closer[0], r: closer[1] }, block) < dist(u, block)) return go(closer);
      if (dist(u, block) === 0) return null; // already in the way: hold
    }
  }
  const chase = [...raidersOf(civ), ...(kindOf(u).canAttack ? (civ.menaces || []) : [])];
  if (chase.length && kindOf(u).canAttack) {
    const goal = chase.slice().sort((a, b) => dist(u, a) - dist(u, b))[0];
    const closer = steps.slice().sort((a, b) => dist({ q: a[0], r: a[1] }, goal) - dist({ q: b[0], r: b[1] }, goal))[0];
    if (dist({ q: closer[0], r: closer[1] }, goal) < dist(u, goal)) return go(closer);
  }
  // Otherwise walk TOWARD something worth reaching: a find on the map, a
  // direction nobody has explored (the whole of the Far Shore, and a bonus
  // and a card for anyone), then fog. The first bot only stepped onto an
  // unseen hex if one was adjacent, so from the square it never left.
  const seen = new Set(civ.seen || []);
  const goals = [
    ...(civ.finds || []).map((f) => ({ q: f.q, r: f.r, w: 3 })),
    ...unexploredDirections(civ).map((d) => { const [q, r] = siteCentre(d, civ); return { q, r, w: goalOf(civ) === 'far_shore' ? 4 : 2 }; }),
  ];
  const towards = (list) => {
    let best = null;
    list.forEach((g) => {
      const here = dist(u, g);
      steps.forEach(([q, r]) => {
        const d = dist({ q, r }, g);
        const score = (here - d) * g.w - (seen.has(`${q},${r}`) ? 0 : -0.3);
        if (d < here && (!best || score > best.score)) best = { q, r, score };
      });
    });
    return best;
  };
  const toward = towards(goals);
  if (toward) return go([toward.q, toward.r]);
  const fresh = steps.filter(([q, r]) => !seen.has(`${q},${r}`));
  if (fresh.length) return go(fresh[0]);
  const paying = steps.find(([q, r]) => {
    const w = workAt(civ, q, r);
    return w && (want[w.resource] || 0) >= 1.5;
  });
  if (paying && !workAt(civ, u.q, u.r)) return go(paying);
  return null;
};

// -------------------------------------------------------------------- Hall
const cultureMove = (state, want) => {
  const civ = civFor(state, 'culture');
  const hall = hallOf(civ);
  if (!hall) return null;

  // Two put forward for an empty chair: take the one worth more. Costs nothing.
  const offered = offerOf(civ).slice().sort((a, b) => seatWorth(civ, b, want) - seatWorth(civ, a, want));
  if (offered.length && councilOf(civ).length < COUNCIL_SEATS
    && ok(state, 'culture', 'choose', { counselor: offered[0] })) {
    return actAction('culture', 'choose', { counselor: offered[0] });
  }
  // (Old saves only: somebody at the door and a chair free.)
  const waiting = waitingOf(civ).slice().sort((a, b) => seatWorth(civ, b, want) - seatWorth(civ, a, want));
  if (waiting.length && councilOf(civ).length < COUNCIL_SEATS
    && ok(state, 'culture', 'seat', { counselor: waiting[0] })) {
    return actAction('culture', 'seat', { counselor: waiting[0] });
  }
  // The council starts FULL, so nobody who arrives ever sits unless the
  // Hall shows somebody out. The first bot never did, and every counselor
  // the world produced waited at the door for the whole game. Show out the
  // seat worth least when someone worth more is waiting.
  if (waiting.length && councilOf(civ).length >= COUNCIL_SEATS) {
    const worst = councilOf(civ).slice().sort((a, b) => seatWorth(civ, a.id, want) - seatWorth(civ, b.id, want))[0];
    if (worst && seatWorth(civ, waiting[0], want) > seatWorth(civ, worst.id, want) + 0.5
      && ok(state, 'culture', 'dismiss', { counselor: worst.id })) {
      return actAction('culture', 'dismiss', { counselor: worst.id });
    }
  }

  if (want.stage >= 5 && ok(state, 'culture', 'build-monument')) return actAction('culture', 'build-monument');

  // The burden pays in something the town is short of: take it (it festers,
  // and the Hall settles it like any grievance).
  if (hall.burden && ok(state, 'culture', 'burden')) {
    const [k, n] = Object.entries(HALL_CARDS[hall.burden]?.bounty || {})[0] || [];
    if (k && (civ[k] || 0) < n && civ.happiness >= 7) return actAction('culture', 'burden');
  }
  // Take up an idea while the allowance lasts.
  if (buysOf(civ) > 0) {
    const affordable = (hall.market || [])
      .filter((c) => ok(state, 'culture', 'buy', { card: c }))
      .sort((a, b) => score(civ, b, want) - score(civ, a, want));
    if (affordable.length) return actAction('culture', 'buy', { card: affordable[0] });
  }

  // The turn: which card onto which chair. A play is free (playtest 14),
  // so the question is only where a card is liked, and what pairs up.
  const seats = councilOf(civ).filter((c) => !seatUsed(civ, c.id));
  const hand = hall.hand || [];
  if (seats.length && hand.length) {
    let best = null;
    hand.forEach((card) => {
      seats.forEach((seat) => {
        const params = { card, counselor: seat.id };
        if (!ok(state, 'culture', 'play', params)) return;
        const v = playValue(civ, card, seat.id, want);
        if (!best || v > best.v) best = { v, params };
      });
    });
    if (best) return actAction('culture', 'play', best.params);
  }
  return null;
};

/** What a counselor's chair is worth: their one liking, priced in what the town wants. */
const seatWorth = (civ, id, want) => {
  const k = COUNSELORS[id];
  if (!k) return 0;
  const likes = k.likes || {};
  let v = 0.5;
  ['happiness', 'knowledge', 'food', 'goods', 'coin'].forEach((key) => { if (likes[key]) v += (want[key] || 0.5) * likes[key]; });
  if (likes.hands) v += likes.hands * 1.5;
  if (likes.draw) v += likes.draw * 0.8;
  if (likes.reveal) v += 1.5;
  if (likes.stone) v += civ.monument?.revealed ? 1 : 0;
  return v;
};

/** Roughly what a shelf card is worth to the town right now, against its price. */
const score = (civ, cardId, want) => {
  const card = HALL_CARDS[cardId];
  if (!card) return 0;
  const gain = card.effect || {};
  let v = 0;
  Object.entries(gain).forEach(([k, n]) => { v += (want[k] || 0.5) * n; });
  if (card.giveHands) v += card.giveHands * 1.5;
  if (card.buys) v += 2;
  if (card.law) v += 1.5;
  if (card.envoy) v += (civ.rivals || []).some((r) => r.met && r.stance === 'hostile') ? 4 : 0.5;
  const price = cardPrice(cardId) || {};
  Object.entries(price).forEach(([k, n]) => { v -= (want[k] || 0.5) * n * 0.6; });
  return v;
};

const playValue = (civ, cardId, counselor, want) => {
  const card = HALL_CARDS[cardId];
  if (!card) return 0;
  // A grievance is a dead card: settling it gets it out of the hand before
  // it festers, but any live card beats it for the seat.
  if (card.grievance) return (bonusesFor(civ, cardId, counselor).length ? 1 : 0) - 2;
  // THE CHARTER. A town produces more than a village but it EATS, so this
  // is a bet rather than an upgrade: worth taking when there is a village
  // grown enough to charter and food enough to carry another mouth.
  if (card.charter) {
    const ready = villagesOf(civ).some((v) => !v.chartered && v.size >= VILLAGE.charterAtSize);
    if (!ready) return -2;
    // A town eats one more food a night for ever, so the question is
    // whether the larder can carry it - not whether it is nice to have.
    const eat = Math.ceil(civ.population / 2);
    return civ.food >= eat + 2 ? 9 : -1;
  }
  let v = 0;
  Object.entries(cardEffect(civ, cardId, counselor)).forEach(([k, n]) => { v += (want[k] || 0.5) * n; });
  v += cardGives(civ, cardId, counselor) * 1.5 + cardDraws(civ, cardId, counselor) * 0.8;
  if (card.buys) v += 2;
  // A law stands for ever: worth its mods for the rest of the era, not a
  // one-off. The first bot priced every law at nothing and fed all of them
  // to counselors face down.
  if (card.law) v += 3 + (want.law || 0);
  if (card.envoy) v += (civ.rivals || []).some((r) => r.met && r.stance === 'hostile') ? 5 : ((civ.rivals || []).some((r) => r.met && r.stance === 'wary') ? 2 : -1);
  return v;
};

export const MOVES = {
  trade: tradeMove, science: scienceMove, military: militaryMove, culture: cultureMove,
};

/** Play one role's whole day. Returns how many actions it took. */
export const playDay = (state, role, dispatch, style) => {
  let s = state;
  let taken = 0;
  for (let i = 0; i < 40; i += 1) {
    const civ = civFor(s, role);
    const want = needs(civ, s.meta);
    const move = MOVES[role](s, want, style, s.meta);
    if (!move) break;
    const next = dispatch(s, move);
    if (next === s) break;            // the reducer refused it; stop rather than spin
    s = next;
    taken += 1;
  }
  return [dispatch(s, doneAction(role)), taken];
};
